import React, {
  createRef,
  useEffect,
  useState,
  Suspense,
  useRef,
  useLayoutEffect,
} from "react";
import {
  Switch,
  Route as RouterRoute,
  useLocation,
  BrowserRouter,
  useHistory,
} from "react-router-dom";
import { connect, useSelector } from "dva";
import PageSpinner from "./components/Spinner/PageSpinner";
import { routes } from "./utils/config";
import { setComputedRoutes } from "./models/router";
import ErrorBoundary from "./components/ErrorBoundary";
import EmptyLayout from "./layouts/EmptyLayout";
import AuthLayout from "./layouts/AuthLayout";
import DashboardLayout from "./layouts/DashboardLayout";
import RootWrapper from "./components/RootWrapper";
import { getRoutePath } from "./utils";
import { getUser } from "./models/auth";

// Create a Context for navigation state management
const NavigationContext = React.createContext({
  isNavigating: false,
  setIsNavigating: () => {},
  navigationError: null,
  setNavigationError: () => {},
});

export const historyRef = createRef();

const routeRenderer = (
  routes,
  prefix = "",
  basePath = "",
  parentConfig = {},
) => {
  const allRoutes = [];
  const computedRoutes = [];

  routes.forEach((route, i) => {
    const routePath = getRoutePath(basePath, route.route?.path || "");
    if (route.subRoutes) {
      const [ar, cr] = routeRenderer(
        route.subRoutes,
        prefix + i + ".",
        routePath,
        route,
      );
      allRoutes.push(...ar);
      computedRoutes.push(...cr);
    }

    if (route.route?.path && route.component) {
      // Create a component that safely handles dynamic imports
      const SafeComponent = () => {
        const Component = route.component;

        // Handle component errors using ErrorBoundary
        const handleComponentError = (error) => {
          console.error("Component load error:", error);
        };

        // Custom fallback UI for component errors
        const errorFallback = (error, retry) => (
          <div className="component-load-error">
            <h3>Error Loading Page</h3>
            <p>{error?.message || "Failed to load the requested page"}</p>
            <div className="error-actions">
              <button onClick={retry}>Try Again</button>
              <button onClick={() => (window.location.href = "/")}>
                Return to Dashboard
              </button>
            </div>
          </div>
        );

        // Wrap the component in ErrorBoundary and Suspense for lazy loading
        return (
          <ErrorBoundary
            onError={handleComponentError}
            fallback={errorFallback}
          >
            <Suspense fallback={<PageSpinner text="Loading Page..." />}>
              <Component />
            </Suspense>
          </ErrorBoundary>
        );
      };

      allRoutes.push(
        <Route
          key={prefix + i}
          layoutType={route.layoutType || parentConfig.layoutType}
          authType={route.authType || parentConfig.authType}
          requiredRoles={route.roles || parentConfig.roles}
          {...route.route}
          path={routePath}
        >
          <SafeComponent />
        </Route>,
      );
      computedRoutes.push({
        ...route,
        key: prefix + i,
        route: { ...route.route, path: routePath },
      });
    }
  });

  return [allRoutes, computedRoutes];
};

export default connect(undefined, { setComputedRoutes })(function Router({
  setComputedRoutes,
}) {
  // Global navigation state management
  const [isNavigating, setIsNavigating] = useState(false);
  const [navigationError, setNavigationError] = useState(null);

  const [ar, cr] = routeRenderer(routes);

  useEffect(() => {
    setComputedRoutes(cr);
  }, [cr, setComputedRoutes]);

  return (
    <NavigationContext.Provider
      value={{
        isNavigating,
        setIsNavigating,
        navigationError,
        setNavigationError,
      }}
    >
      <BrowserRouter>
        <LocationChangeListener />
        <RootWrapper>
          <ErrorBoundary
            fallback={(error, retry) => (
              <div className="app-error">
                <h2>Application Error</h2>
                <p>
                  {error?.message ||
                    "The application encountered an unexpected error."}
                </p>
                <button onClick={retry}>Try Again</button>
              </div>
            )}
          >
            <Suspense fallback={<PageSpinner text="Loading Application..." />}>
              <Switch>{ar}</Switch>
            </Suspense>
          </ErrorBoundary>
        </RootWrapper>
      </BrowserRouter>
    </NavigationContext.Provider>
  );
});

// Component to track location changes
const LocationChangeListener = () => {
  const location = useLocation();
  const { setIsNavigating } = React.useContext(NavigationContext);
  const prevLocationRef = useRef(location);

  useEffect(() => {
    // Only pathname change triggers full page navigation state.
    // Query parameter (search) changes within the same page should not unmount the route.
    if (location.pathname !== prevLocationRef.current.pathname) {
      // Set navigating flag
      setIsNavigating(true);

      // Use requestAnimationFrame to wait for the next paint
      // This allows the navigation spinner to render before heavy work begins
      requestAnimationFrame(() => {
        // Use another rAF to ensure we're after the paint
        requestAnimationFrame(() => {
          setIsNavigating(false);
          prevLocationRef.current = location;
        });
      });
    } else {
      prevLocationRef.current = location;
    }
  }, [location, setIsNavigating]);

  return null;
};

const LayoutWrapper = ({ type, children }) => {
  switch (type) {
    case "empty":
      return <EmptyLayout>{children}</EmptyLayout>;
    case "auth":
      return <AuthLayout>{children}</AuthLayout>;
    case "dashboard":
      return <DashboardLayout>{children}</DashboardLayout>;
    case "none":
    default:
      return children;
  }
};

const AuthWrapper = connect(() => ({}), { getUser: getUser })(({
  type,
  children,
  getUser,
}) => {
  //login as shop owner from super admin
  useLayoutEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get("accessToken");
    const refreshToken = params.get("refreshToken");
    const userId = params.get("userId");
    if (accessToken && refreshToken && userId) {
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("userId", userId);
    }
  }, []);

  const location = useLocation();
  const history = useHistory();
  const [token, setToken] = useState(() => localStorage.getItem("accessToken"));
  const [refreshToken, setRefreshToken] = useState(() =>
    localStorage.getItem("refreshToken"),
  );
  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    if (user == null && (token || refreshToken)) {
      const userId = localStorage.getItem("userId");
      if (userId !== null) {
        getUser(userId);
      }
    }
  }, [user, token, refreshToken, getUser]);

  useEffect(() => {
    const checkAuthAndRedirect = () => {
      const currentToken = localStorage.getItem("accessToken");
      const currentRefreshToken = localStorage.getItem("refreshToken");

      // Redirect to /orders if user is shop-chef
      if (
        user &&
        user.role === "shop-chef" &&
        location.pathname !== "/orders"
      ) {
        history.replace("/orders?status=confirmed", { from: location });
        return;
      }

      // POS operators can access the POS (old and new) and tables screens.
      if (
        user &&
        (user.role === "shop-pos-operator" || user.role === "shop-waiter") &&
        !["/pos-new"].includes(location.pathname)
      ) {
        history.replace("/pos-new", { from: location });
        return;
      }

      // Waiters can access the tables screen and the new POS (dine-in only).
      // if (
      //   user &&
      //   user.role === "shop-waiter" &&
      //   !["/tables", "/pos-new"].includes(location.pathname)
      // ) {
      //   history.replace("/tables", { from: location });
      //   return;
      // }

      if (
        type === "only-authenticated" &&
        !currentToken &&
        !currentRefreshToken
      ) {
        history.replace("/login", { from: location });
      } else if (
        type === "only-unauthenticated" &&
        (currentToken || currentRefreshToken)
      ) {
        history.replace("/", { from: location });
      }
    };

    // Initial check
    checkAuthAndRedirect();

    // Listen for storage changes from other tabs
    const handleStorage = (e) => {
      if (e.key === "accessToken" || e.key === "refreshToken") {
        setToken(localStorage.getItem("accessToken"));
        setRefreshToken(localStorage.getItem("refreshToken"));
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [type, location.pathname, history, user]); // Depend on user state and path changes

  // Also update local state whenever user state changes (from login/logout effects)
  useEffect(() => {
    setToken(localStorage.getItem("accessToken"));
    setRefreshToken(localStorage.getItem("refreshToken"));
  }, [user]);

  return children;
});

const Route = ({
  authType = "none",
  layoutType = "empty",
  requiredRoles,
  children,
  ...props
}) => {
  const history = useHistory();
  const { isNavigating, navigationError } = React.useContext(NavigationContext);

  // Only set historyRef once on component mount
  useEffect(() => {
    if (!historyRef.current) {
      historyRef.current = history;
    }
    // No cleanup needed - we want to keep the ref available
  }, [history]);

  // If there was a navigation error, show an error message
  if (navigationError) {
    return (
      <RouterRoute {...props}>
        <ErrorBoundary>
          <div className="navigation-error">
            <h3>Navigation Error</h3>
            <p>
              {navigationError?.message ||
                "There was a problem loading this page. Please try again."}
            </p>
            <div className="error-actions">
              <button onClick={() => window.location.reload()}>
                Reload Page
              </button>
              <button onClick={() => history.push("/")}>Return Home</button>
            </div>
          </div>
        </ErrorBoundary>
      </RouterRoute>
    );
  }

  // Render the route with error boundaries and suspense
  return (
    <RouterRoute {...props}>
      <ErrorBoundary>
        <AuthWrapper type={authType} requiredRoles={requiredRoles}>
          <LayoutWrapper type={layoutType}>
            <Suspense
              fallback={
                <div className="component-loading">
                  <PageSpinner text="Loading..." />
                </div>
              }
            >
              {isNavigating ? (
                <div className="navigation-loading">
                  <PageSpinner text="Navigating..." />
                </div>
              ) : (
                children
              )}
            </Suspense>
          </LayoutWrapper>
        </AuthWrapper>
      </ErrorBoundary>
    </RouterRoute>
  );
};
